import { useState } from "react"
import { Chip } from "@/components/Molecules/Chip/Chip"
import { Icon } from "@/components/Atoms/Icon"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer"
import { Button } from "@/components/ui/button"

export interface UsersFilterProps {
  search: string
  onSearchChange: (value: string) => void
  statusFilter: string
  onStatusFilterChange: (value: string) => void
  profileFilter: string
  onProfileFilterChange: (value: string) => void
  typeFilter: string
  onTypeFilterChange: (value: string) => void
}

const STATUS_LABELS: Record<string, string> = {
  ativos: "Ativos",
  inativos: "Inativos",
}

const PROFILE_LABELS: Record<string, string> = {
  Membro: "Membro",
  Liderança: "Liderança",
  Colaborador: "Colaborador",
  Convidado: "Convidado",
}

const TYPE_LABELS: Record<string, string> = {
  "0": "Padrão",
  "1": "PAG",
}

export const UsersFilter: React.FC<UsersFilterProps> = ({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  profileFilter,
  onProfileFilterChange,
  typeFilter,
  onTypeFilterChange,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [draftStatus, setDraftStatus] = useState("all")
  const [draftProfile, setDraftProfile] = useState("all")
  const [draftType, setDraftType] = useState("all")

  const hasActiveFilters =
    statusFilter !== "all" || profileFilter !== "all" || typeFilter !== "all"

  const openDrawer = () => {
    setDraftStatus(statusFilter)
    setDraftProfile(profileFilter)
    setDraftType(typeFilter)
    setDrawerOpen(true)
  }

  const applyFilters = () => {
    onStatusFilterChange(draftStatus)
    onProfileFilterChange(draftProfile)
    onTypeFilterChange(draftType)
    setDrawerOpen(false)
  }

  const clearFilters = () => {
    onStatusFilterChange("all")
    onProfileFilterChange("all")
    onTypeFilterChange("all")
    setDraftStatus("all")
    setDraftProfile("all")
    setDraftType("all")
    setDrawerOpen(false)
  }

  const activeChips: { key: string; label: string; onRemove: () => void }[] = []

  if (statusFilter !== "all") {
    activeChips.push({
      key: "status",
      label: STATUS_LABELS[statusFilter] ?? statusFilter,
      onRemove: () => onStatusFilterChange("all"),
    })
  }

  if (profileFilter !== "all") {
    activeChips.push({
      key: "profile",
      label: PROFILE_LABELS[profileFilter] ?? profileFilter,
      onRemove: () => onProfileFilterChange("all"),
    })
  }

  if (typeFilter !== "all") {
    activeChips.push({
      key: "type",
      label: TYPE_LABELS[typeFilter] ?? typeFilter,
      onRemove: () => onTypeFilterChange("all"),
    })
  }

  return (
    <div>
      {/* Mobile layout */}
      <div className="lg:hidden">
        <div className="flex items-center gap-3">
          <div className="relative flex flex-1 items-center gap-3 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Buscar por nome ou e-mail..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2.5 focus:outline-none"
            />
          </div>
          <button
            onClick={openDrawer}
            className="relative flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-black/40 transition-colors"
          >
            <i
              className={`fi fi-ts-sliders-v flex items-center justify-center text-base ${hasActiveFilters ? "text-orange-500" : "text-white"}`}
            />
            {hasActiveFilters && (
              <span className="absolute -right-1 -top-1 flex size-3 items-center justify-center rounded-full bg-orange-500" />
            )}
          </button>
        </div>

        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onClose={chip.onRemove} />
            ))}
          </div>
        )}
      </div>

      {/* Desktop layout */}
      <div className="hidden lg:block">
        <div className="flex items-center gap-4">
          <div className="relative flex w-80 items-center gap-4 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Buscar por nome ou e-mail..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2 focus:outline-none"
            />
          </div>

          <Select onValueChange={onStatusFilterChange} value={statusFilter}>
            <SelectTrigger
              className={`w-40 border-border bg-surface ${statusFilter !== "all" ? "border-orange-500 bg-orange-500/10 text-orange-500" : ""}`}
            >
              <SelectValue placeholder="Todos os Status" />
            </SelectTrigger>
            <SelectContent className="border-border bg-surface">
              <SelectItem value="all">Todos os Status</SelectItem>
              <SelectItem value="ativos">Ativos</SelectItem>
              <SelectItem value="inativos">Inativos</SelectItem>
            </SelectContent>
          </Select>

          <Select onValueChange={onProfileFilterChange} value={profileFilter}>
            <SelectTrigger
              className={`w-40 border-border bg-surface ${profileFilter !== "all" ? "border-orange-500 bg-orange-500/10 text-orange-500" : ""}`}
            >
              <SelectValue placeholder="Todos os Perfis" />
            </SelectTrigger>
            <SelectContent className="border-border bg-surface">
              <SelectItem value="all">Todos os Perfis</SelectItem>
              <SelectItem value="Membro">Membro</SelectItem>
              <SelectItem value="Liderança">Liderança</SelectItem>
              <SelectItem value="Colaborador">Colaborador</SelectItem>
              <SelectItem value="Convidado">Convidado</SelectItem>
            </SelectContent>
          </Select>

          <Select onValueChange={onTypeFilterChange} value={typeFilter}>
            <SelectTrigger
              className={`w-40 border-border bg-surface ${typeFilter !== "all" ? "border-orange-500 bg-orange-500/10 text-orange-500" : ""}`}
            >
              <SelectValue placeholder="Todos os Tipos" />
            </SelectTrigger>
            <SelectContent className="border-border bg-surface">
              <SelectItem value="all">Todos os Tipos</SelectItem>
              <SelectItem value="0">Padrão</SelectItem>
              <SelectItem value="1">PAG</SelectItem>
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 whitespace-nowrap text-sm text-muted-foreground transition-colors hover:text-orange-500"
            >
              Limpar filtros
            </button>
          )}
        </div>

        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onClose={chip.onRemove} />
            ))}
          </div>
        )}
      </div>

      {/* Mobile bottom sheet */}
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent className="border-white/10 bg-surface px-4 pb-2">
          <DrawerHeader>
            <DrawerTitle className="text-white">Filtros</DrawerTitle>
          </DrawerHeader>

          <div className="flex flex-col gap-4 px-4 pb-2">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Status</span>
              <Select onValueChange={setDraftStatus} value={draftStatus}>
                <SelectTrigger
                  className={`border-border bg-black/40 ${draftStatus !== "all" ? "border-orange-500 bg-orange-500/10 text-orange-500" : "text-white"}`}
                >
                  <SelectValue placeholder="Todos os Status" />
                </SelectTrigger>
                <SelectContent className="border-border bg-surface">
                  <SelectItem value="all">Todos os Status</SelectItem>
                  <SelectItem value="ativos">Ativos</SelectItem>
                  <SelectItem value="inativos">Inativos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Perfil</span>
              <Select onValueChange={setDraftProfile} value={draftProfile}>
                <SelectTrigger
                  className={`border-border bg-black/40 ${draftProfile !== "all" ? "border-orange-500 bg-orange-500/10 text-orange-500" : "text-white"}`}
                >
                  <SelectValue placeholder="Todos os Perfis" />
                </SelectTrigger>
                <SelectContent className="border-border bg-surface">
                  <SelectItem value="all">Todos os Perfis</SelectItem>
                  <SelectItem value="Membro">Membro</SelectItem>
                  <SelectItem value="Liderança">Liderança</SelectItem>
                  <SelectItem value="Colaborador">Colaborador</SelectItem>
                  <SelectItem value="Convidado">Convidado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Tipo</span>
              <Select onValueChange={setDraftType} value={draftType}>
                <SelectTrigger
                  className={`border-border bg-black/40 ${draftType !== "all" ? "border-orange-500 bg-orange-500/10 text-orange-500" : "text-white"}`}
                >
                  <SelectValue placeholder="Todos os Tipos" />
                </SelectTrigger>
                <SelectContent className="border-border bg-surface">
                  <SelectItem value="all">Todos os Tipos</SelectItem>
                  <SelectItem value="0">Padrão</SelectItem>
                  <SelectItem value="1">PAG</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DrawerFooter>
            <Button
              onClick={applyFilters}
              className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600"
            >
              Aplicar filtro
            </Button>
            <Button
              onClick={clearFilters}
              variant="ghost"
              className="w-full text-muted-foreground hover:text-white"
            >
              Limpar filtro
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  )
}
